"use client";

import { useState, useCallback } from "react";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { Maximize2, Minimize2 } from "lucide-react";
import styles from "./scientificCalculator.module.css";

function formatDisplay(value: string): string {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "0";
  if (!Number.isFinite(num)) return "Error";
  const s = String(value);
  if (s.length > 14) return num.toExponential(6);
  return s;
}

function factorial(n: number): number {
  if (n < 0 || n !== Math.floor(n)) return NaN;
  if (n > 170) return Infinity;
  if (n <= 1) return 1;
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

export default function ScientificCalculator() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isFullScreen = searchParams.get("fullscreen") === "1";

  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [memory, setMemory] = useState(0);
  const [isDeg, setIsDeg] = useState(true);
  const [isNewInput, setIsNewInput] = useState(true);

  const getDisplayNum = useCallback(() => {
    const n = parseFloat(display);
    return Number.isNaN(n) ? 0 : n;
  }, [display]);

  const toRad = useCallback(
    (x: number) => (isDeg ? (x * Math.PI) / 180 : x),
    [isDeg]
  );

  const computeResult = useCallback(() => {
    const current = getDisplayNum();
    if (previousValue === null || operation === null) return current;
    switch (operation) {
      case "+":
        return previousValue + current;
      case "−":
        return previousValue - current;
      case "×":
        return previousValue * current;
      case "÷":
        return current === 0 ? NaN : previousValue / current;
      case "y^x":
        return Math.pow(previousValue, current);
      case "xrooty":
        return current === 0 ? NaN : Math.pow(previousValue, 1 / current);
      default:
        return current;
    }
  }, [previousValue, operation, getDisplayNum]);

  const handleNumber = (digit: string) => {
    if (isNewInput) {
      setDisplay(digit === "." ? "0." : digit);
      setIsNewInput(false);
    } else {
      if (digit === "." && display.includes(".")) return;
      if (digit === "." && display === "0") {
        setDisplay("0.");
        return;
      }
      if (display === "0" && digit !== ".") {
        setDisplay(digit);
        return;
      }
      if (display.length >= 16) return;
      setDisplay(display + digit);
    }
  };

  const handleOperator = (op: string) => {
    if (previousValue !== null && operation !== null) {
      const result = computeResult();
      if (Number.isNaN(result) || !Number.isFinite(result)) {
        setDisplay("Error");
        setIsNewInput(true);
        setPreviousValue(null);
        setOperation(null);
        return;
      }
      const resultStr = String(result);
      setDisplay(resultStr.length > 14 ? result.toExponential(6) : resultStr);
      setPreviousValue(result);
      setOperation(op);
    } else {
      setPreviousValue(getDisplayNum());
      setOperation(op);
    }
    setIsNewInput(true);
  };

  const handleEquals = () => {
    const result = computeResult();
    if (Number.isNaN(result) || !Number.isFinite(result)) {
      setDisplay("Error");
    } else {
      const resultStr = String(result);
      setDisplay(resultStr.length > 14 ? result.toExponential(6) : resultStr);
    }
    setPreviousValue(null);
    setOperation(null);
    setIsNewInput(true);
  };

  const handleUnary = (fn: (x: number) => number) => {
    const n = getDisplayNum();
    const r = fn(n);
    if (Number.isNaN(r) || !Number.isFinite(r)) {
      setDisplay("Error");
    } else {
      setDisplay(String(r));
    }
    setIsNewInput(true);
  };

  const handleClear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setIsNewInput(true);
  };

  const handleSign = () => handleUnary((x) => -x);
  const handleReciprocal = () => handleUnary((x) => (x === 0 ? NaN : 1 / x));
  const handleSquare = () => handleUnary((x) => x * x);
  const handleCube = () => handleUnary((x) => x * x * x);
  const handleSqrt = () => handleUnary((x) => (x < 0 ? NaN : Math.sqrt(x)));
  const handleFactorial = () => handleUnary(factorial);
  const handleLog = () => handleUnary((x) => (x <= 0 ? NaN : Math.log10(x)));
  const handleLn = () => handleUnary((x) => (x <= 0 ? NaN : Math.log(x)));
  const handleExp = () => handleUnary((x) => Math.exp(x));
  const handleSin = () => handleUnary((x) => Math.sin(toRad(x)));
  const handleCos = () => handleUnary((x) => Math.cos(toRad(x)));
  const handleTan = () => handleUnary((x) => Math.tan(toRad(x)));
  const handleSinh = () => handleUnary((x) => Math.sinh(x));
  const handleCosh = () => handleUnary((x) => Math.cosh(x));
  const handleTanh = () => handleUnary((x) => Math.tanh(x));

  const handlePower = () => {
    setPreviousValue(getDisplayNum());
    setOperation("y^x");
    setIsNewInput(true);
  };

  const handleXRootY = () => {
    setPreviousValue(getDisplayNum());
    setOperation("xrooty");
    setIsNewInput(true);
  };

  const handlePercent = () => {
    const n = getDisplayNum();
    if (previousValue !== null && operation !== null) {
      const pct = (previousValue * n) / 100;
      const result =
        operation === "+"
          ? previousValue + pct
          : operation === "−"
            ? previousValue - pct
            : operation === "×"
              ? previousValue * (n / 100)
              : previousValue / (n / 100 || 1);
      setDisplay(String(result));
      setPreviousValue(null);
      setOperation(null);
    } else {
      setDisplay(String(n / 100));
    }
    setIsNewInput(true);
  };

  const handlePi = () => {
    setDisplay(String(Math.PI));
    setIsNewInput(true);
  };

  const handleEE = () => {
    if (display.includes("e")) return;
    setDisplay(display + "e");
    setIsNewInput(false);
  };

  const handleRnd = () => {
    setDisplay(String(Math.random()));
    setIsNewInput(true);
  };

  const handleMemoryClear = () => setMemory(0);
  const handleMemoryRecall = () => {
    setDisplay(String(memory));
    setIsNewInput(true);
  };
  const handleMemoryAdd = () => setMemory(memory + getDisplayNum());
  const handleMemorySub = () => setMemory(memory - getDisplayNum());

  const toggleFullScreen = () => {
    if (isFullScreen) {
      router.replace(`${pathname}`);
    } else {
      router.replace(`${pathname}?fullscreen=1`);
    }
  };

  const displayValue = formatDisplay(display);

  return (
    <div className={`${styles.wrapper} ${isFullScreen ? styles.fullScreen : ""}`}>
      <div className={styles.calcContainer}>
        <div className={styles.display} aria-live="polite">
          {displayValue}
        </div>
        <div className={styles.mode}>{isDeg ? "Deg" : "Rad"}</div>

        <div className={styles.grid}>
          <button type="button" className={styles.btnMode} onClick={() => setIsDeg(!isDeg)} aria-label="Toggle Deg/Rad">
            {isDeg ? "Deg" : "Rad"}
          </button>
          <button type="button" className={styles.btnFunc} disabled aria-label="Open parenthesis">(</button>
          <button type="button" className={styles.btnFunc} disabled aria-label="Close parenthesis">)</button>
          <button type="button" className={styles.btnFunc} onClick={handlePercent} aria-label="Percent">%</button>
          <button type="button" className={styles.btnMemory} onClick={handleMemoryClear} aria-label="Memory clear">MC</button>
          <button type="button" className={styles.btnMemory} onClick={handleMemoryAdd} aria-label="Memory add">M+</button>
          <button type="button" className={styles.btnMemory} onClick={handleMemorySub} aria-label="Memory subtract">M−</button>
          <button type="button" className={styles.btnMemory} onClick={handleMemoryRecall} aria-label="Memory recall">MR</button>

          <button type="button" className={styles.btnFunc} onClick={handleReciprocal}>1/x</button>
          <button type="button" className={styles.btnFunc} onClick={handleSquare}>x²</button>
          <button type="button" className={styles.btnFunc} onClick={handleCube}>x³</button>
          <button type="button" className={styles.btnFunc} onClick={handlePower} aria-label="y to the power x">yˣ</button>
          <button type="button" className={styles.btnClear} onClick={handleClear}>C</button>
          <button type="button" className={styles.btnFunc} onClick={handleSign}>+/−</button>
          <button type="button" className={styles.btnOp} onClick={() => handleOperator("÷")}>÷</button>
          <button type="button" className={styles.btnOp} onClick={() => handleOperator("×")}>×</button>

          <button type="button" className={styles.btnFunc} onClick={handleFactorial}>x!</button>
          <button type="button" className={styles.btnFunc} onClick={handleSqrt}>√</button>
          <button type="button" className={styles.btnFunc} onClick={handleXRootY} aria-label="x-th root of y">ˣ√y</button>
          <button type="button" className={styles.btnFunc} onClick={handleLog}>log</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("7")}>7</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("8")}>8</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("9")}>9</button>
          <button type="button" className={styles.btnOp} onClick={() => handleOperator("−")}>−</button>

          <button type="button" className={styles.btnFunc} onClick={handleSin}>sin</button>
          <button type="button" className={styles.btnFunc} onClick={handleCos}>cos</button>
          <button type="button" className={styles.btnFunc} onClick={handleTan}>tan</button>
          <button type="button" className={styles.btnFunc} onClick={handleLn}>ln</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("4")}>4</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("5")}>5</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("6")}>6</button>
          <button type="button" className={styles.btnOp} onClick={() => handleOperator("+")}>+</button>

          <button type="button" className={styles.btnFunc} onClick={handleSinh}>sinh</button>
          <button type="button" className={styles.btnFunc} onClick={handleCosh}>cosh</button>
          <button type="button" className={styles.btnFunc} onClick={handleTanh}>tanh</button>
          <button type="button" className={styles.btnFunc} onClick={handleExp}>eˣ</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("1")}>1</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("2")}>2</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("3")}>3</button>
          <button type="button" className={styles.btnEquals} onClick={handleEquals} aria-label="Equals">=</button>

          <button type="button" className={styles.btnFunc} onClick={() => setIsDeg(!isDeg)}>1st</button>
          <button type="button" className={styles.btnFunc} onClick={handlePi}>π</button>
          <button type="button" className={styles.btnFunc} onClick={handleEE}>EE</button>
          <button type="button" className={styles.btnFunc} onClick={handleRnd}>rnd</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber("0")}>0</button>
          <button type="button" className={styles.btnNum} onClick={() => handleNumber(".")}>.</button>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btnFullScreen} onClick={toggleFullScreen} aria-label={isFullScreen ? "Exit full screen" : "Use full screen"}>
            {isFullScreen ? <Minimize2 size={18} aria-hidden /> : <Maximize2 size={18} aria-hidden />}
            <span>{isFullScreen ? "Exit Full Screen" : "Use the Scientific Calculator Full Screen"}</span>
          </button>
        </div>
      </div>

      {!isFullScreen && (
        <p className={styles.backLink}>
          <Link href="/categories/calculator-tools">← Back to Calculator tools</Link>
        </p>
      )}
    </div>
  );
}
